import { Injectable } from '@angular/core';
import { PhoneService } from '@fmnts/api/next';
import { ConnectionService, hasConnectionStatus } from '@fmnts/common';
import {
  ActionDispatcher,
  ActionEvent,
  ActionHandler,
  ActionRequest,
  ActionResponse,
  ActionResponseError,
  ActionStartEvent,
} from '@fmnts/common/actions';
import {
  isNonEmptyString,
  isNotNullish,
  isRecord,
  isString,
} from '@fmnts/core';
import { TranslationService } from '@fmnts/i18n';
import { NextCampaignStore } from '@formunauts/next/campaign/data-access';
import {
  Observable,
  catchError,
  concat,
  filter,
  map,
  of,
  switchMap,
  throwError,
  withLatestFrom,
} from 'rxjs';

export enum PhoneAction {
  MobileNumberValidation = 'mobile_number_validation',
}

export interface ValidateMobileNumberArgs {
  /** phone number */
  phoneNumber: string;
  /** ISO language as string, e.g. 'de' */
  language?: string;
}

/**
 * Checks if the passed `args` are valid `ValidateMobileNumberArgs` and returns them
 * if they fulfill the interface. If not `undefined` is returned.
 */
function assertValidateMobileNumberArgs(
  args: unknown,
): ValidateMobileNumberArgs | undefined {
  if (!isRecord(args)) {
    return;
  }

  const { phoneNumber, language } = args;
  if (!isString(phoneNumber)) {
    return;
  }

  return {
    phoneNumber,
    language: isNonEmptyString(language) ? language : undefined,
  };
}

/**
 * `ActionHandler` for phone related actions.
 */
@Injectable()
export class PhoneActionHandler implements ActionHandler {
  public readonly knownActions: PhoneAction[] = [
    PhoneAction.MobileNumberValidation,
  ];

  constructor(
    private phoneService: PhoneService,
    private connectionSerivce: ConnectionService,
    private translationService: TranslationService,
    private campaignStore: NextCampaignStore,
  ) {}

  handleAction(
    req: ActionRequest<unknown>,
    next: ActionDispatcher,
  ): Observable<ActionEvent<unknown>> {
    switch (req.action) {
      case PhoneAction.MobileNumberValidation:
        return this.validateMobile(req);
      default:
        return next.dispatch(req);
    }
  }

  isExecuteable(action: string, next: ActionDispatcher): Observable<boolean> {
    switch (action) {
      case PhoneAction.MobileNumberValidation:
        return this.canValidateMobile();
      default:
        return next.isExecuteable(action);
    }
  }

  private validateMobile(req: ActionRequest<unknown>) {
    const args = assertValidateMobileNumberArgs(req.args);
    // Check for arguments
    if (!args) {
      return throwError(
        () => new ActionResponseError(req, 'Passed args are not valid'),
      );
    }

    const action$ = of(args).pipe(
      withLatestFrom(
        this.campaignStore.currentCampaign$.pipe(
          filter(isNotNullish),
          map((campaign) => campaign.id),
        ),
        this.translationService.currentLanguage$,
      ),
      switchMap(([{ phoneNumber, language }, campaignId, globalLanguage]) =>
        this.phoneService
          .sendSms(phoneNumber, campaignId, language ?? globalLanguage)
          .pipe(
            catchError((e) => of(e)),
            map((r) => new ActionResponse(req, r.status)),
          ),
      ),
    );

    return concat(
      // First, emit the action start event
      of(new ActionStartEvent(req)),
      // then run the actual action
      action$,
    );
  }

  /**
   * @returns
   * `Observable` that emits with `boolean` value
   * that is `true` when a mobile phone number can
   * be validated.
   */
  public canValidateMobile() {
    return this.connectionSerivce.connectionStatus$.pipe(
      hasConnectionStatus('online'),
    );
  }
}
